<script lang="ts" setup>

import { useRoute } from 'vue-router';

import { computed } from '#imports';

type Props = {
    isLoading?: boolean;
    total: number;
    page: number;
    size: number;
    visibleItems: number;
};

const props = defineProps<Props>();

const { fullPath } = useRoute();

const totalPages = computed(() => props.total ? Math.ceil(props.total / props.size) : 0);

const pages = computed(() => {
    const pagesArray: (string | number)[] = [1];
    const middleItems = Math.floor(props.visibleItems / 2);

    if (props.page > middleItems + 2) {
        pagesArray.push('...');
    }

    const start = Math.max(
        2,
        props.page - middleItems
    );
    const end = Math.min(
        totalPages.value - 1,
        props.page + middleItems
    );

    for (let i = start; i <= end; i++) {
        pagesArray.push(i);
    }

    if (props.page < totalPages.value - middleItems - 1) {
        pagesArray.push('...');
    }

    if (totalPages.value > 1) pagesArray.push(totalPages.value);

    return pagesArray;
});

const emit = defineEmits<{
    (e: 'onPage', payload: number): void;
}>();

const handlePage = (currentPage: string | number, index: number) => {
    if (typeof currentPage === 'number') {
        emit(
            'onPage',
            currentPage
        );
    }
    else {
        if (index === 1) {
            const newPage = Math.max(
                props.page - props.visibleItems,
                1
            );
            emit(
                'onPage',
                newPage
            );
        }
        else {
            const newPage = Math.min(
                props.page + props.visibleItems,
                totalPages.value
            );
            emit(
                'onPage',
                newPage
            );
        }
    }
};

const onNextPage = async () => {
    if (props.page < totalPages.value) {
        emit(
            'onPage',
            props.page + 1
        );
    }
};

const onPrevPage = async () => {
    if (props.page > 1) {
        emit(
            'onPage',
            props.page - 1
        );
    }
};

const buildPageLink = (paramPage: number | string): string => {
    const domain = import.meta.env.VITE_DOMEN_URL;
    const url = new URL(
        fullPath,
        domain
    );
    const searchParams = new URLSearchParams(url.search);
    searchParams.set(
        'page',
        paramPage.toString()
    );
    searchParams.set(
        'size',
        props.size.toString()
    );
    url.search = searchParams.toString();
    return url.pathname + url.search;
};

const previousLink = computed(() => buildPageLink(Math.max(
    1,
    props.page - 1
)));
const nextLink = computed(() => buildPageLink(Math.min(
    totalPages.value,
    props.page + 1
)));
</script>

<template>
  <nav v-if="pages.length > 1"
       aria-label="Page navigation"
       class="display-flex flex-row align-items-center paginate gap-8"
       v-bind="$attrs"
  >
    <a :href="previousLink"
       class="paginate__item paginate__arrow btn-bg-mojito display-flex align-items-center justify-center "
       :class="{ 'paginate__arrow--disabled': page === 1}"
       :aria-label="props.page > 1 ? `Previous: Page ${props.page - 1}` : 'Previous: Page'"
       :title="props.page > 1 ? `Go to page ${props.page - 1}` : 'Go to first page'"
       rel="prev"
       @click.prevent="onPrevPage"
    >
      <IconMain :size="24"
                icon="arrow-l-r"
      />
    </a>
    <LoaderDots v-if="isLoading || !pages.length" />
    <ol v-else-if="pages.length >= 1"
        class="display-flex flex-row"
    >
      <li v-for="(item, index) in pages"
          :key="item"
          class="paginate__item"
          :class="{ 'paginate__item--active': page === item }"
      >
        <a class="width-full display-flex align-items-center text-500-default justify-center height-full"
           :aria-current="page === item ? 'page' : undefined"
           :aria-label="`Go to page ${item}`"
           :title="`Go to page ${item}`"
           @click.prevent="handlePage(item, index)"
        >{{ item }}</a>
      </li>
    </ol>

    <a class="paginate__item paginate__arrow btn-bg-mojito display-flex align-items-center justify-center"
       :class="{ 'paginate__arrow--disabled': page === totalPages}"
       :href="nextLink"
       :aria-label="`Next: Page ${props.page + 1}`"
       :title="`Go to page ${props.page + 1}`"
       @click.prevent="onNextPage"
    >
      <IconMain :size="24"
                icon="arrow-r-l"
      />
    </a>
  </nav>
</template>

<style lang="scss" scoped>
.paginate {
  &__arrow {
    &--disabled {
      background-color: var(--bg-tertary);
      pointer-events: none;
    }
  }

  &__item {
    min-width: 30px;
    height: 30px;
    padding-right: 4px;
    padding-left: 4px;
    border-radius: var(--control-500-radius, 2px);
    cursor: pointer;

    &--active {
      background-color: var(--bg-secondary);

      a {
        color: var(--bg-tertary)
      }
    }
  }
}
</style>
